import React, { createContext, useReducer, useEffect } from "react";

const STORAGE_KEYS = {
  token: ["authToken", "token", "trading_app_token"],
  user: ["userData", "trading_app_user"],
};
const ACCOUNT_STORE_KEY = "gainwise_registered_accounts";

// Auth Context
const AuthContext = createContext();

// Auth Actions
const AUTH_ACTIONS = {
  LOGIN_START: "LOGIN_START",
  LOGIN_SUCCESS: "LOGIN_SUCCESS",
  LOGIN_FAILURE: "LOGIN_FAILURE",
  LOGOUT: "LOGOUT",
  REGISTER_START: "REGISTER_START",
  REGISTER_SUCCESS: "REGISTER_SUCCESS",
  REGISTER_FAILURE: "REGISTER_FAILURE",
  UPDATE_USER: "UPDATE_USER",
  VERIFY_EMAIL_START: "VERIFY_EMAIL_START",
  VERIFY_EMAIL_SUCCESS: "VERIFY_EMAIL_SUCCESS",
  VERIFY_EMAIL_FAILURE: "VERIFY_EMAIL_FAILURE",
  RESET_PASSWORD_START: "RESET_PASSWORD_START",
  RESET_PASSWORD_SUCCESS: "RESET_PASSWORD_SUCCESS",
  RESET_PASSWORD_FAILURE: "RESET_PASSWORD_FAILURE",
  CLEAR_ERROR: "CLEAR_ERROR",
  SET_LOADING: "SET_LOADING",
  INIT_AUTH: "INIT_AUTH",
};

const getFirstStoredValue = (keys) => {
  try {
    for (const key of keys) {
      const value = localStorage.getItem(key);
      if (value) {
        return value;
      }
    }
  } catch {
    return null;
  }

  return null;
};

const normalizeUser = (user = {}) => {
  const firstName = user.firstName || user.name || "";
  const lastName = user.lastName || "";
  const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();

  return {
    ...user,
    firstName,
    lastName,
    email: user.email || "",
    name: fullName || firstName || user.email || "Trader",
  };
};

const getStoredAccounts = () => {
  try {
    const value = localStorage.getItem(ACCOUNT_STORE_KEY);
    return value ? JSON.parse(value) : [];
  } catch {
    return [];
  }
};

const saveStoredAccounts = (accounts) => {
  try {
    localStorage.setItem(ACCOUNT_STORE_KEY, JSON.stringify(accounts));
  } catch (error) {
    console.warn("Failed to save registered accounts:", error);
  }
};

const hashPassword = async (password) => {
  const value = password || "";

  if (globalThis.crypto?.subtle && globalThis.TextEncoder) {
    const encoded = new TextEncoder().encode(value);
    const digest = await globalThis.crypto.subtle.digest("SHA-256", encoded);

    return Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join("");
  }

  return btoa(value);
};

const createLocalToken = () => {
  if (globalThis.crypto?.randomUUID) {
    return `local-token-${globalThis.crypto.randomUUID()}`;
  }

  return `local-token-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 10)}`;
};

const clearStoredAuth = () => {
  try {
    [...STORAGE_KEYS.token, ...STORAGE_KEYS.user].forEach((key) =>
      localStorage.removeItem(key)
    );
    localStorage.removeItem("wisefirstName");
  } catch (error) {
    console.warn("Failed to clear auth data from localStorage:", error);
  }
};

const storeAuth = (user, token) => {
  if (!user || !token) {
    clearStoredAuth();
    return;
  }

  const normalizedUser = normalizeUser(user);

  try {
    localStorage.setItem("authToken", token);
    localStorage.setItem("token", token);
    localStorage.setItem("trading_app_token", token);
    localStorage.setItem("userData", JSON.stringify(normalizedUser));
    localStorage.setItem("trading_app_user", JSON.stringify(normalizedUser));
    localStorage.setItem("wisefirstName", normalizedUser.firstName || "");
  } catch (error) {
    console.warn("Failed to sync auth data to localStorage:", error);
  }
};

const parseStoredUser = (value) => {
  if (!value) {
    return null;
  }

  try {
    return normalizeUser(JSON.parse(value));
  } catch {
    return null;
  }
};

// Get initial token from localStorage only once
const getInitialToken = () => {
  return getFirstStoredValue(STORAGE_KEYS.token);
};

// Get initial user data from localStorage only once
const getInitialUser = () => {
  return parseStoredUser(getFirstStoredValue(STORAGE_KEYS.user));
};

// Initial State - Get data once at initialization
const initialState = {
  user: getInitialUser(),
  token: getInitialToken(),
  isAuthenticated: !!(getInitialToken() && getInitialUser()),
  isLoading: true,
  isLoginLoading: false,
  isRegisterLoading: false,
  isVerifyingEmail: false,
  isResettingPassword: false,
  error: null,
  message: null,
};

// Auth Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.INIT_AUTH:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: action.payload.isAuthenticated,
        isLoading: false,
      };

    case AUTH_ACTIONS.LOGIN_START:
      return {
        ...state,
        isLoginLoading: true,
        error: null,
        message: null,
      };

    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoginLoading: false,
        isLoading: false,
        error: null,
        message: action.payload.message || "Login successful",
      };

    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoginLoading: false,
        isLoading: false,
        error: action.payload,
      };

    case AUTH_ACTIONS.REGISTER_START:
      return {
        ...state,
        isRegisterLoading: true,
        error: null,
        message: null,
      };

    case AUTH_ACTIONS.REGISTER_SUCCESS:
      return {
        ...state,
        isRegisterLoading: false,
        error: null,
        message:
          action.payload.message ||
          "Registration successful! Please verify your email.",
      };

    case AUTH_ACTIONS.REGISTER_FAILURE:
      return {
        ...state,
        isRegisterLoading: false,
        error: action.payload,
      };

    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        message: null,
      };

    case AUTH_ACTIONS.UPDATE_USER:
      return {
        ...state,
        user: { ...state.user, ...action.payload },
        message: "Profile updated successfully",
      };

    case AUTH_ACTIONS.VERIFY_EMAIL_START:
      return {
        ...state,
        isVerifyingEmail: true,
        error: null,
        message: null,
      };

    case AUTH_ACTIONS.VERIFY_EMAIL_SUCCESS:
      return {
        ...state,
        isVerifyingEmail: false,
        user: state.user ? { ...state.user, emailVerified: true } : null,
        message: action.payload.message || "Email verified successfully",
      };

    case AUTH_ACTIONS.VERIFY_EMAIL_FAILURE:
      return {
        ...state,
        isVerifyingEmail: false,
        error: action.payload,
      };

    case AUTH_ACTIONS.RESET_PASSWORD_START:
      return {
        ...state,
        isResettingPassword: true,
        error: null,
        message: null,
      };

    case AUTH_ACTIONS.RESET_PASSWORD_SUCCESS:
      return {
        ...state,
        isResettingPassword: false,
        message: action.payload.message || "Password reset email sent",
      };

    case AUTH_ACTIONS.RESET_PASSWORD_FAILURE:
      return {
        ...state,
        isResettingPassword: false,
        error: action.payload,
      };

    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
        message: null,
      };

    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };

    default:
      return state;
  }
};

// Auth Context Provider
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize auth state on app load - runs only once
  useEffect(() => {
    const initializeAuth = async () => {
      const token = state.token;
      const user = state.user;

      if (token && user) {
        try {
          // Optional: Verify token with server if you have an API
          // const response = await api.get('/auth/me');
          // For now, just use stored data
          dispatch({
            type: AUTH_ACTIONS.INIT_AUTH,
            payload: {
              user: user,
              token: token,
              isAuthenticated: true,
            },
          });
        } catch (error) {
          // Token is invalid, clear everything
          clearStoredAuth();
          dispatch({
            type: AUTH_ACTIONS.INIT_AUTH,
            payload: {
              user: null,
              token: null,
              isAuthenticated: false,
            },
          });
        }
      } else {
        dispatch({
          type: AUTH_ACTIONS.INIT_AUTH,
          payload: {
            user: null,
            token: null,
            isAuthenticated: false,
          },
        });
      }
    };

    initializeAuth();
  }, []); // Empty dependency array - runs only once

  // Sync state changes to localStorage
  useEffect(() => {
    storeAuth(state.user, state.token);
  }, [state.user, state.token]);

  // Login function
  const login = async (email, password) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });

    try {
      const normalizedEmail = email.trim().toLowerCase();
      const passwordHash = await hashPassword(password);
      const accounts = getStoredAccounts();
      const account = accounts.find(
        (entry) =>
          entry.email.toLowerCase() === normalizedEmail &&
          entry.passwordHash === passwordHash
      );

      if (!account) {
        throw new Error("Invalid email or password");
      }

      const user = normalizeUser({
        id: account.id,
        firstName: account.firstName,
        lastName: account.lastName,
        email: account.email,
        phone: account.phone,
        country: account.country,
        createdAt: account.createdAt,
      });
      const token = createLocalToken();

      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: {
          user,
          token,
          message: "Login successful",
        },
      });

      return { success: true, data: { user, token } };
    } catch (error) {
      const errorMessage = error.message || "Login failed. Please try again.";

      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: errorMessage,
      });

      return { success: false, error: errorMessage };
    }
  };

  // Register function
  const register = async (userData) => {
    dispatch({ type: AUTH_ACTIONS.REGISTER_START });

    try {
      const registrationPayload = {
        firstName: userData.firstName?.trim(),
        lastName: userData.lastName?.trim(),
        email: userData.email?.trim(),
        password: userData.password,
        phone: userData.phone?.trim() || "",
        country: userData.country?.trim() || "",
        agreeToMarketing: !!userData.agreeToMarketing,
      };
      const normalizedEmail = registrationPayload.email.toLowerCase();
      const accounts = getStoredAccounts();
      const emailExists = accounts.some(
        (account) => account.email.toLowerCase() === normalizedEmail
      );

      if (emailExists) {
        throw new Error("An account with this email already exists");
      }

      const passwordHash = await hashPassword(registrationPayload.password);
      const account = {
        id:
          globalThis.crypto?.randomUUID?.() ||
          `user-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        firstName: registrationPayload.firstName,
        lastName: registrationPayload.lastName,
        email: registrationPayload.email,
        phone: registrationPayload.phone,
        country: registrationPayload.country,
        agreeToMarketing: registrationPayload.agreeToMarketing,
        passwordHash,
        createdAt: new Date().toISOString(),
      };

      saveStoredAccounts([...accounts, account]);
      const loginResult = await login(
        registrationPayload.email,
        registrationPayload.password
      );

      if (!loginResult.success) {
        throw new Error(loginResult.error || "Login after registration failed.");
      }

      dispatch({
        type: AUTH_ACTIONS.REGISTER_SUCCESS,
        payload: {
          message: "Registration successful",
        },
      });

      return {
        success: true,
        data: {
          message: "Registration successful",
          user: loginResult.data.user,
          token: loginResult.data.token,
        },
      };
    } catch (error) {
      const errorMessage = error.message || "Registration failed. Please try again.";

      dispatch({
        type: AUTH_ACTIONS.REGISTER_FAILURE,
        payload: errorMessage,
      });

      return { success: false, error: errorMessage };
    }
  };

  // Logout function
  const logout = async () => {
    try {
      // Call logout endpoint to invalidate token on server if needed
      // await api.post('/auth/logout');
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      clearStoredAuth();
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    }
  };

  // Update user profile
  const updateUser = async (userData) => {
    try {
      const updatedUser = normalizeUser({ ...state.user, ...userData });
      const accounts = getStoredAccounts();
      const nextAccounts = accounts.map((account) =>
        account.email.toLowerCase() === state.user?.email?.toLowerCase()
          ? {
              ...account,
              firstName: updatedUser.firstName,
              lastName: updatedUser.lastName,
              email: updatedUser.email,
              phone: updatedUser.phone || "",
              country: updatedUser.country || "",
            }
          : account
      );
      saveStoredAccounts(nextAccounts);

      dispatch({
        type: AUTH_ACTIONS.UPDATE_USER,
        payload: updatedUser,
      });

      return { success: true, data: { user: updatedUser } };
    } catch (error) {
      const errorMessage = error.message || "Profile update failed.";
      return { success: false, error: errorMessage };
    }
  };

  // Clear error/message
  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  // Check if user has permission
  const hasPermission = (permission) => {
    if (!state.user) return false;
    return (
      state.user.permissions?.includes(permission) ||
      state.user.role === "admin"
    );
  };

  // Context value
  const value = {
    // State
    user: state.user,
    token: state.token,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    isLoginLoading: state.isLoginLoading,
    isRegisterLoading: state.isRegisterLoading,
    isVerifyingEmail: state.isVerifyingEmail,
    isResettingPassword: state.isResettingPassword,
    error: state.error,
    message: state.message,
    loading: state.isLoading, // Alias for compatibility

    // Functions
    login,
    register,
    logout,
    updateUser,
    clearError,
    hasPermission,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Export AuthContext as named export
export { AuthContext };

export default AuthContext;
